import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { 
  House, 
  Users, 
  GridFour, 
  CalendarBlank, 
  ChartBar, 
  Heart, 
  Stack,
  Gear,
  SignOut 
} from '@phosphor-icons/react'

export default function AdminNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [organization, setOrganization] = useState(null)

  useEffect(() => {
    loadUserAndOrg()
  }, [])

  const loadUserAndOrg = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: userData } = await supabase
        .from('users')
        .select('name, email, role, org_id, profile_photo_url, avatar_url')
        .eq('id', authUser.id)
        .single()

      setUser(userData)

      // Load organization
      if (userData?.org_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name, logo_url')
          .eq('id', userData.org_id)
          .single()

        setOrganization(orgData)
      }
    } catch (error) {
      console.error('Load user error:', error)
    }
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const navItems = [
    {
      name: 'Home',
      path: '/admin',
      icon: House
    },
    {
      name: 'Members',
      path: '/admin/members',
      icon: Users
    },
    {
      name: 'Structure',
      path: '/admin/hierarchy',
      icon: Stack
    },
    {
      name: 'Events',
      path: '/admin/events',
      icon: CalendarBlank
    },
    {
      name: 'Reports',
      path: '/admin/reports',
      icon: ChartBar
    },
    {
      name: 'Affirmations',
      path: '/admin/affirmations',
      icon: Heart
    },
    {
      name: 'Settings',
      path: '/admin/settings',
      icon: Gear
    }
  ]

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <nav className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          {organization?.logo_url ? (
            <img 
              src={organization.logo_url} 
              alt={organization.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-bold text-lg">
                {organization?.name?.charAt(0) || 'O'}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">
              {organization?.name || 'Organization'}
            </h1>
            <p className="text-xs text-gray-500">Admin Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-3">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.path)
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={20} weight={active ? 'fill' : 'light'} />
                {item.name}
              </Link>
            )
          })}
        </div>
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200">
        {user && (
          <div className="mb-3">
            <div className="flex items-center gap-3">
              {user.profile_photo_url || user.avatar_url ? (
                <img 
                  src={user.profile_photo_url || user.avatar_url} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    {user.name?.charAt(0) || 'A'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        >
          <SignOut size={16} weight="light" />
          Logout
        </button>
      </div>
    </nav>
  )
}